import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { callZohoAPI } from '../utils/api';
import { Sparkles } from 'lucide-react';
import { discoverRelatedListName } from '../services/crmApi';
const modules = [{
  label: 'Contacts',
  value: 'Contacts'
}, {
  label: 'Tags',
  value: 'Tags'
}, {
  label: 'Categories',
  value: 'Categories'
}, {
  label: 'Mailing Lists',
  value: 'Mailing_Lists'
}, {
  label: '<Custom>',
  value: '__custom__'
}];
const moduleDefaultFields: Record<string, string> = {
  Contacts: 'id,Email,First_Name,Last_Name,Full_Name,Tags,Region,Sub_Region,Team,Bucket_ID,Bucket_ID_Expires_At,Status,Form_Name,Phone,Mobile,Created_Time,Modified_Time,Owner,Title,Department',
  Tags: 'id,Name,Team,Region,Created_Time,Modified_Time',
  Categories: 'id,Name,Description,Team,Region,Created_Time,Modified_Time',
  Mailing_Lists: 'id,Name,Description,Status,Subscriber_Count,Team,Region,Sub_Region,Created_Time,Modified_Time'
};
const relatedModules = [{
  label: 'Notes',
  value: 'Notes'
}, {
  label: 'Categories',
  value: 'Categories14'
}, {
  label: 'Mailing Lists',
  value: 'Mailing_Lists3'
}, {
  label: 'Tags',
  value: 'Tags1'
}, {
  label: 'Contacts',
  value: 'Contacts'
}];
const CONTACT_FIELDS_FOR_TEST = 'id,Email,First_Name,Last_Name,Full_Name,Tags,Region,Sub_Region,Team,Bucket_ID,Status,Form_Name,Phone';
const tabs = [{
  id: 'list',
  label: 'List Records'
}, {
  id: 'details',
  label: 'Record Details'
}, {
  id: 'reverse',
  label: 'Reverse Lookup'
}, {
  id: 'settings',
  label: 'Settings'
}] as const;
type TabId = (typeof tabs)[number]['id'];
function ResponseBlock({
  response,
  error,
  onClear
}: {
  response: any;
  error: string | null;
  onClear: () => void;
}) {
  return <>
      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-id="element-2242" data-mp="10wm9ybfv8m7p">
          <p className="text-sm font-medium text-red-800" data-id="element-2243" data-mp="swojt89hxjzz">Error</p>
          <p className="text-sm text-red-700 mt-1 font-mono" data-id="element-2244" data-mp="suvgo2y3dhwq">{error}</p>
        </div>}
      {response && <div data-id="element-2245" data-mp="ota5cm17hbon">
          <div className="flex items-center justify-between mb-2" data-id="element-2246" data-mp="12mhv085q7x3p">
            <p className="text-sm font-medium text-gray-700" data-id="element-2247" data-mp="12lpzexc4pzdf">
              Response
              {response?.info?.count != null && <span className="ml-2 text-xs text-gray-500" data-id="element-2248" data-mp="9xkdi79ca4c0">
                  ({response.info.count} records, page {response.info.page},
                  more: {String(response.info.more_records)})
                </span>}
            </p>
            <button onClick={onClear} className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2249" data-mp="jxbbwk10t3hd">
              Clear
            </button>
          </div>
          <div className="bg-gray-900 rounded-lg p-5 overflow-auto max-h-[70vh]" data-id="element-2250" data-mp="1qclpnq0xsmik">
            <pre className="text-sm text-green-400 font-mono whitespace-pre-wrap break-words" data-id="element-2251" data-mp="389ytwai5j9db">
              {JSON.stringify(response, null, 2)}
            </pre>
          </div>
        </div>}
    </>;
}
export function ApiTest() {
  const [selectedModule, setSelectedModule] = useState('Contacts');
  const [customModule, setCustomModule] = useState('');
  const [activeTab, setActiveTab] = useState<TabId>('list');
  const [loading, setLoading] = useState<string | null>(null);
  const [customFields, setCustomFields] = useState('');
  // Tab 1 state
  const [listResponse, setListResponse] = useState<any>(null);
  const [listError, setListError] = useState<string | null>(null);
  // Tab 2 state
  const [detailResponse, setDetailResponse] = useState<any>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [recordId, setRecordId] = useState('');
  const [relatedModule, setRelatedModule] = useState('Notes');
  const [relatedFields, setRelatedFields] = useState('ID');
  // Tab 3 (Reverse Lookup) state
  const [reverseResponse, setReverseResponse] = useState<any>(null);
  const [reverseError, setReverseError] = useState<string | null>(null);
  const [reverseParentModule, setReverseParentModule] = useState('Mailing_Lists');
  const [reverseParentId, setReverseParentId] = useState('');
  const [reverseChildModule, setReverseChildModule] = useState('Contacts');
  const [discoveredApiName, setDiscoveredApiName] = useState<string | null>(null);
  const [discoveryLog, setDiscoveryLog] = useState<string>('');
  // Tab 4 state
  const [settingsResponse, setSettingsResponse] = useState<any>(null);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const effectiveModule = selectedModule === '__custom__' ? customModule.trim() : selectedModule;
  const moduleLabel = selectedModule === '__custom__' ? customModule.trim() || 'Custom' : modules.find(m => m.value === selectedModule)?.label || selectedModule;
  const effectiveFields = customFields.trim() || moduleDefaultFields[effectiveModule] || '';
  const fieldsParam = effectiveFields ? `fields=${effectiveFields}&` : '';
  const listUrl = `crm/v8/${effectiveModule}?${fieldsParam}per_page=200`;
  const detailUrl = `crm/v8/${effectiveModule}/${recordId.trim() || '{ID}'}`;
  const relatedUrl = `crm/v8/${effectiveModule}/${recordId.trim() || '{ID}'}/${relatedModule}?fields=${relatedFields.trim() || 'ID'}`;
  const relatedListsUrl = `crm/v8/settings/related_lists?module=${effectiveModule}`;
  const fieldsUrl = `crm/v8/settings/fields?module=${effectiveModule}`;
  const handleFetchList = async () => {
    if (!effectiveModule) return;
    setLoading('list');
    setListError(null);
    setListResponse(null);
    try {
      const data = await callZohoAPI(listUrl);
      setListResponse(data);
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(null);
    }
  };
  const handleFetchDetails = async () => {
    if (!recordId.trim() || !effectiveModule) return;
    setLoading('detail');
    setDetailError(null);
    setDetailResponse(null);
    try {
      const data = await callZohoAPI(`crm/v8/${effectiveModule}/${recordId.trim()}`);
      setDetailResponse(data);
    } catch (err) {
      setDetailError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(null);
    }
  };
  const handleFetchRelated = async () => {
    if (!recordId.trim() || !effectiveModule) return;
    setLoading('related');
    setDetailError(null);
    setDetailResponse(null);
    try {
      const data = await callZohoAPI(`crm/v8/${effectiveModule}/${recordId.trim()}/${relatedModule}?fields=${relatedFields.trim() || 'ID'}`);
      setDetailResponse(data);
    } catch (err) {
      setDetailError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(null);
    }
  };
  const handleDiscoverAndReverseLookup = async () => {
    if (!reverseParentId.trim()) return;
    setLoading('reverse');
    setReverseError(null);
    setReverseResponse(null);
    setDiscoveryLog('');
    setDiscoveredApiName(null);
    try {
      // Step 1: Discover the correct related list API name using v8 Settings API
      setDiscoveryLog('Step 1: Discovering related list API name (v8 API)...');
      // Check hardcoded names first
      const hardcodedNames: Record<string, string> = {
        'Mailing_Lists:Contacts': 'Contacts5',
        'Categories:Contacts': 'Contacts4'
      };
      const hardcodedKey = `${reverseParentModule}:${reverseChildModule}`;
      const hardcodedName = hardcodedNames[hardcodedKey];
      if (hardcodedName) {
        setDiscoveryLog(prev => prev + `\n✓ Using known API name: "${hardcodedName}" (from CRM Settings > API Names)`);
        setDiscoveredApiName(hardcodedName);
        // Step 2: Fetch related records using the known name with v8 API
        // The `fields` parameter is MANDATORY for the v8 Related Records API
        setDiscoveryLog(prev => prev + `\n\nStep 2: Fetching via crm/v8/${reverseParentModule}/${reverseParentId.trim()}/${hardcodedName}?fields=${reverseChildModule},Email,Name...`);
        const url = `crm/v8/${reverseParentModule}/${reverseParentId.trim()}/${hardcodedName}?fields=${reverseChildModule},Email,Name,Owner&per_page=200`;
        const data = await callZohoAPI(url);
        const records = data?.data || [];
        const count = records.length;
        // Extract actual contact IDs from junction records
        // Junction records have a lookup field (e.g., "Contacts": {name, id}) pointing to the real record
        const contactIds: string[] = [];
        for (const record of records) {
          // Look for the child module lookup field
          const lookupField = record[reverseChildModule];
          if (lookupField && typeof lookupField === 'object' && lookupField.id) {
            contactIds.push(lookupField.id);
          }
        }
        if (contactIds.length > 0) {
          setDiscoveryLog(prev => prev + `\n✓ Success! Found ${count} junction record(s) → ${contactIds.length} actual ${reverseChildModule} IDs` + `\n\nExtracted ${reverseChildModule} IDs:\n  ${contactIds.join('\n  ')}`);
        } else {
          setDiscoveryLog(prev => prev + `\n✓ Success! Found ${count} record(s)`);
        }
        setReverseResponse(data);
        return;
      }
      // No hardcoded name — discover via v8 Settings API
      const settingsResponse = await callZohoAPI(`crm/v8/settings/related_lists?module=${reverseParentModule}`);
      const relatedLists = settingsResponse?.related_lists || [];
      // Strategy 1: Match by module.api_name
      let matches = relatedLists.filter((rl: any) => rl.module?.api_name === reverseChildModule);
      // Strategy 2: Match by display name
      if (matches.length === 0) {
        matches = relatedLists.filter((rl: any) => rl.name === reverseChildModule || rl.name === reverseChildModule.replace(/_/g, ' '));
      }
      // Strategy 3: Match by api_name pattern (e.g., "Contacts5" starts with "Contacts")
      if (matches.length === 0) {
        const pattern = new RegExp(`^${reverseChildModule}\\d*$`);
        matches = relatedLists.filter((rl: any) => pattern.test(rl.api_name));
      }
      const matchInfo = matches.map((m: any) => `"${m.api_name}" (name: "${m.name}", type: ${m.type || 'unknown'}, module: ${m.module?.api_name || 'unknown'})`).join(', ');
      setDiscoveryLog(prev => prev + `\nFound ${matches.length} relation(s): ${matchInfo || 'none'}`);
      if (matches.length === 0) {
        const allNames = relatedLists.map((rl: any) => `"${rl.api_name}" → ${rl.module?.api_name || 'unknown'} (${rl.name})`).join('\n  ');
        setDiscoveryLog(prev => prev + `\n\n⚠ No "${reverseChildModule}" relation found! All available relations:\n  ${allNames}`);
        setReverseError(`No "${reverseChildModule}" relation found on ${reverseParentModule}. Check the discovery log for available relations.`);
        return;
      }
      // Pick best match
      let bestMatch = matches[0];
      for (const match of matches) {
        if (match.type === 'multiselectlookup') {
          bestMatch = match;
          break;
        }
        if (/\d+$/.test(match.api_name) && !/\d+$/.test(bestMatch.api_name)) {
          bestMatch = match;
        }
      }
      const apiName = bestMatch.api_name;
      setDiscoveredApiName(apiName);
      setDiscoveryLog(prev => prev + `\n✓ Using: "${apiName}"`);
      // Step 2: Fetch related records using v8 API
      setDiscoveryLog(prev => prev + `\n\nStep 2: Fetching ${reverseChildModule} via crm/v8/${reverseParentModule}/${reverseParentId.trim()}/${apiName}?fields=${reverseChildModule},Email,Name...`);
      const url = `crm/v8/${reverseParentModule}/${reverseParentId.trim()}/${apiName}?fields=${reverseChildModule},Email,Name,Owner&per_page=200`;
      const data = await callZohoAPI(url);
      const records = data?.data || [];
      const count = records.length;
      // Extract actual contact IDs from junction records
      const contactIds: string[] = [];
      for (const record of records) {
        const lookupField = record[reverseChildModule];
        if (lookupField && typeof lookupField === 'object' && lookupField.id) {
          contactIds.push(lookupField.id);
        }
      }
      if (contactIds.length > 0) {
        setDiscoveryLog(prev => prev + `\n✓ Success! Found ${count} junction record(s) → ${contactIds.length} actual ${reverseChildModule} IDs` + `\n\nExtracted ${reverseChildModule} IDs:\n  ${contactIds.join('\n  ')}`);
      } else {
        setDiscoveryLog(prev => prev + `\n✓ Success! Found ${count} record(s)`);
      }
      setReverseResponse(data);
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Unknown error';
      setDiscoveryLog(prev => prev + `\n✗ Error: ${errMsg}`);
      setReverseError(errMsg);
    } finally {
      setLoading(null);
    }
  };
  const handleFetchRelatedLists = async () => {
    if (!effectiveModule) return;
    setLoading('relatedLists');
    setSettingsError(null);
    setSettingsResponse(null);
    try {
      const data = await callZohoAPI(`crm/v8/settings/related_lists?module=${effectiveModule}`);
      setSettingsResponse(data);
    } catch (err) {
      setSettingsError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(null);
    }
  };
  const handleFetchFields = async () => {
    if (!effectiveModule) return;
    setLoading('fields');
    setSettingsError(null);
    setSettingsResponse(null);
    try {
      const data = await callZohoAPI(`crm/v8/settings/fields?module=${effectiveModule}`);
      setSettingsResponse(data);
    } catch (err) {
      setSettingsError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(null);
    }
  };
  return <div className="space-y-6" data-id="element-2252" data-mp="379mxt1qbu20z">
      <div className="flex items-center justify-between" data-id="element-2253" data-mp="37crc4rxiae5d">
        <div data-id="element-2254" data-mp="3cbccqsgo6zuc">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-2255" data-mp="3caj23gelbq6x">
            Zoho API Test
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-2256" data-mp="3gatjdp7u2313">
            Temporary page for testing Zoho API calls.
          </p>
        </div>
        <Link to="/api-test/gpt" className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700" data-id="element-2257" data-mp="1lxlgrbdm8n4w">
          <Sparkles className="h-4 w-4" data-id="element-2258" data-mp="19ncdymv78oi3" />
          Test GPT
        </Link>
      </div>

      {/* Module Selector */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3" data-id="element-2259" data-mp="14nrembc4ae4u">
        <div data-id="element-2260" data-mp="10mo13vhjku6s">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2261" data-mp="10n7inb3a5gka">
            Module
          </label>
          <div className="flex items-center gap-3" data-id="element-2262" data-mp="1xd6pn13s870r">
            <select value={selectedModule} onChange={e => setSelectedModule(e.target.value)} className="block w-64 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" data-id="element-2263" data-mp="1xau82p1xryo8">
              {modules.map(mod => <option key={mod.value} value={mod.value} data-id="element-2264" data-mp="1jfi4rt9pszvp">
                  {mod.label}
                </option>)}
            </select>
            {selectedModule === '__custom__' && <input type="text" value={customModule} onChange={e => setCustomModule(e.target.value)} placeholder="Enter module name" className="block w-64 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" data-id="element-2265" data-mp="80d0wlxukvai" />}
          </div>
        </div>
        <div data-id="element-2266" data-mp="4e9mao193r05">
          <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-2267" data-mp="4hxe9ir1s5cr">
            Fields Override (leave empty for module defaults)
          </label>
          <input type="text" value={customFields} onChange={e => setCustomFields(e.target.value)} placeholder={effectiveFields || 'e.g. id,Email,First_Name,Last_Name,Tags'} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-xs font-mono" data-id="element-2268" data-mp="3rltz72jrwkgi" />
          {effectiveFields && !customFields.trim() && <p className="mt-1 text-xs text-gray-400" data-id="element-2269" data-mp="2p8hjzt9d6dd0">
              Using defaults: {effectiveFields.substring(0, 80)}...
            </p>}
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3" data-id="element-2270" data-mp="2kazao1rfpw4h">
          <p className="text-xs text-amber-800" data-id="element-2271" data-mp="2ya8qh0bqao7x">
            <strong data-id="element-2272" data-mp="2y6unbpv8qazf">Note:</strong> Mailing_List and Categories are{' '}
            <strong data-id="element-2273" data-mp="2u6an2dfder92">multiselect lookup fields</strong> — they cannot be fetched
            inline. Use the <strong data-id="element-2274" data-mp="385shp9fckyqc">Reverse Lookup</strong> tab to get contacts
            from a specific mailing list or category. It auto-discovers the
            correct API name.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-2275" data-mp="3hyzi0fv8dojk">
        <nav className="-mb-px flex gap-6" data-id="element-2276" data-mp="3cz4ummanzh39">
          {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${activeTab === tab.id ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2277" data-mp="3qxbkpg9dgcsd">
              {tab.label}
            </button>)}
        </nav>
      </div>

      {/* Tab 1: List Records */}
      {activeTab === 'list' && <div className="space-y-6" data-id="element-2278" data-mp="1b5cc047q5oio">
          <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3" data-id="element-2279" data-mp="1b4b2i46cxa32">
            <h2 className="text-sm font-medium text-gray-900" data-id="element-2280" data-mp="eg4t73yhptig">
              Fetch {moduleLabel} List
            </h2>
            <p className="text-xs text-gray-500 font-mono break-all" data-id="element-2281" data-mp="ihq8evyhyjvt">
              GET {listUrl}
            </p>
            <button onClick={handleFetchList} disabled={loading !== null} className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2282" data-mp="nep59m9jp4xp">
              {loading === 'list' ? <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-2283" data-mp="3ob9a217we5mj" />
                  Fetching...
                </> : `Fetch ${moduleLabel}`}
            </button>
          </div>
          <ResponseBlock response={listResponse} error={listError} onClear={() => {
        setListResponse(null);
        setListError(null);
      }} data-id="element-2284" data-mp="3act3qbdy6h4x" />
        </div>}

      {/* Tab 2: Record Details */}
      {activeTab === 'details' && <div className="space-y-6" data-id="element-2285" data-mp="2vn3q3goo4knr">
          <div className="bg-white border border-gray-200 rounded-lg p-5" data-id="element-2286" data-mp="2vo6dhems84yx">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2287" data-mp="2qple1hfctpbj">
              Record ID
            </label>
            <input type="text" value={recordId} onChange={e => setRecordId(e.target.value)} placeholder={`Enter ${moduleLabel} ID`} className="block w-64 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" data-id="element-2288" data-mp="2mmfawinjge9a" />
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3" data-id="element-2289" data-mp="xjr30n8gin1j">
            <h2 className="text-sm font-medium text-gray-900" data-id="element-2290" data-mp="xlkag4qw9bal">
              Fetch {moduleLabel} Details (All Fields)
            </h2>
            <p className="text-xs text-gray-500 font-mono" data-id="element-2291" data-mp="nltd7vay8i6t">GET {detailUrl}</p>
            <button onClick={handleFetchDetails} disabled={loading !== null || !recordId.trim()} className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2292" data-mp="iv388j6fqhcn">
              {loading === 'detail' ? <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-2293" data-mp="3w1d1sh0odcfj" />
                  Fetching...
                </> : `Fetch ${moduleLabel} Details`}
            </button>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3" data-id="element-2294" data-mp="3qylzb9xw4l2z">
            <h2 className="text-sm font-medium text-gray-900" data-id="element-2295" data-mp="3vwdr83yjc776">
              Fetch Related Records
            </h2>
            <p className="text-xs text-gray-500 font-mono" data-id="element-2296" data-mp="4n89mrdmu5rp">GET {relatedUrl}</p>
            <div className="flex items-center gap-3" data-id="element-2297" data-mp="338huiz8yufae">
              <select value={relatedModule} onChange={e => setRelatedModule(e.target.value)} className="block w-48 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" data-id="element-2298" data-mp="385i5nokoa88d">
                {relatedModules.map(mod => <option key={mod.value} value={mod.value} data-id="element-2299" data-mp="2kfe2fibecahw">
                    {mod.label}
                  </option>)}
              </select>
              <button onClick={handleFetchRelated} disabled={loading !== null || !recordId.trim()} className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2300" data-mp="2heucmwoqfj7s">
                {loading === 'related' ? <>
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-2301" data-mp="1yfq73xweg7kv" />
                    Fetching...
                  </> : 'Fetch Related Records'}
              </button>
            </div>
            <div data-id="element-2302" data-mp="23f2see7mh6lc">
              <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-2303" data-mp="23ejnby32mcoq">
                Fields (comma-separated)
              </label>
              <input type="text" value={relatedFields} onChange={e => setRelatedFields(e.target.value)} placeholder="ID,First_Name,Last_Name" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" data-id="element-2304" data-mp="3jrro5o941mgy" />
            </div>
          </div>

          <ResponseBlock response={detailResponse} error={detailError} onClear={() => {
        setDetailResponse(null);
        setDetailError(null);
      }} data-id="element-2305" data-mp="3dedzfbdxgoyi" />
        </div>}

      {/* Tab 3: Reverse Lookup (Auto-Discovery) */}
      {activeTab === 'reverse' && <div className="space-y-6" data-id="element-2306" data-mp="e01z0ex07f65">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-2307" data-mp="72ic9lhjex0">
            <p className="text-sm text-blue-800" data-id="element-2308" data-mp="484h6pbhpoyg">
              <strong data-id="element-2309" data-mp="46tdsqlyij7v">Reverse Lookup with Auto-Discovery</strong> — This
              automatically discovers the correct related list API name (e.g.,
              "Contacts3" instead of "Contacts") by calling the Settings API
              first, then fetches the related records.
            </p>
            <p className="text-xs text-blue-600 mt-2" data-id="element-2310" data-mp="356avi31ct0om">
              This is exactly how the Campaign Builder finds contacts for
              mailing lists and categories.
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4" data-id="element-2311" data-mp="2v7u1syb167yv">
            <div className="grid grid-cols-2 gap-4" data-id="element-2312" data-mp="2r6rya5a2z9r3">
              <div data-id="element-2313" data-mp="3nvzmjl233wyp">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2314" data-mp="3nwity7u3z2yh">
                  Parent Module
                </label>
                <select value={reverseParentModule} onChange={e => {
              setReverseParentModule(e.target.value);
              setDiscoveredApiName(null);
              setDiscoveryLog('');
            }} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" data-id="element-2315" data-mp="3ixfz1g7ued48">
                  <option value="Mailing_Lists" data-id="element-2316" data-mp="5o9yz5novl1a">Mailing Lists</option>
                  <option value="Categories" data-id="element-2317" data-mp="1oefoug2bwguq">Categories</option>
                  <option value="Tags" data-id="element-2318" data-mp="1tbpffz1geyk5">Tags</option>
                </select>
              </div>
              <div data-id="element-2319" data-mp="1xdayk24l092s">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2320" data-mp="1xcreofvdfam4">
                  Child Module
                </label>
                <select value={reverseChildModule} onChange={e => {
              setReverseChildModule(e.target.value);
              setDiscoveredApiName(null);
              setDiscoveryLog('');
            }} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" data-id="element-2321" data-mp="22e6yk4ltbb2x">
                  <option value="Contacts" data-id="element-2322" data-mp="2q2hwayw7oumz">Contacts</option>
                </select>
              </div>
            </div>
            <div data-id="element-2323" data-mp="2v11in0epw8fv">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2324" data-mp="2v2mf4vkle1gd">
                Parent Record ID
              </label>
              <input type="text" value={reverseParentId} onChange={e => setReverseParentId(e.target.value)} placeholder="Enter Mailing List / Category ID" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" data-id="element-2325" data-mp="uzl6fo8rjl2x" />
            </div>

            {discoveredApiName && <div className="bg-green-50 border border-green-200 rounded-md p-3" data-id="element-2326" data-mp="1drnp154ytbft">
                <p className="text-xs text-green-800" data-id="element-2327" data-mp="19qaj6v1vh88w">
                  <strong data-id="element-2328" data-mp="1nmxts0mqzwxa">Discovered API name:</strong>{' '}
                  <code className="bg-green-100 px-1 rounded" data-id="element-2329" data-mp="1iowb3gt8hzmc">
                    {discoveredApiName}
                  </code>
                </p>
                <p className="text-xs text-green-600 mt-1" data-id="element-2330" data-mp="3pg57j5mpq6h2">
                  URL:{' '}
                  <code className="text-xs" data-id="element-2331" data-mp="3khar2ac7wu04">
                    crm/v2/{reverseParentModule}/{reverseParentId.trim()}/
                    {discoveredApiName}
                  </code>
                </p>
              </div>}

            <button onClick={handleDiscoverAndReverseLookup} disabled={loading !== null || !reverseParentId.trim()} className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2332" data-mp="24wjovpet1uu2">
              {loading === 'reverse' ? <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-2333" data-mp="1m4h1zwbklibd" />
                  Discovering &amp; Fetching...
                </> : `Discover & Get ${reverseChildModule}`}
            </button>
          </div>

          {/* Discovery Log */}
          {discoveryLog && <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-2334" data-mp="36m56ri7nxpg6">
              <p className="text-xs font-medium text-gray-700 mb-2" data-id="element-2335" data-mp="1uca4pd0xpwtx">
                Discovery Log
              </p>
              <pre className="text-xs text-gray-600 font-mono whitespace-pre-wrap" data-id="element-2336" data-mp="1peqque85fm2i">
                {discoveryLog}
              </pre>
            </div>}

          <ResponseBlock response={reverseResponse} error={reverseError} onClear={() => {
        setReverseResponse(null);
        setReverseError(null);
      }} data-id="element-2337" data-mp="1foxg1fftzazb" />
        </div>}

      {/* Tab 4: Settings */}
      {activeTab === 'settings' && <div className="space-y-6" data-id="element-2338" data-mp="11ppbs84t9co9">
          <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3" data-id="element-2339" data-mp="11qqpylh4ifa7">
            <h2 className="text-sm font-medium text-gray-900" data-id="element-2340" data-mp="wre2byifh46k">
              Related Lists for {moduleLabel}
            </h2>
            <p className="text-xs text-gray-500 font-mono" data-id="element-2341" data-mp="2w0b26916zovw">
              GET {relatedListsUrl}
            </p>
            <button onClick={handleFetchRelatedLists} disabled={loading !== null} className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2342" data-mp="2ynd6ebp4mg42">
              {loading === 'relatedLists' ? <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-2343" data-mp="3ddu5vpi7avyb" />
                  Fetching...
                </> : 'Show Related Lists'}
            </button>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3" data-id="element-2344" data-mp="2pku7u3sdipnm">
            <h2 className="text-sm font-medium text-gray-900" data-id="element-2345" data-mp="2um9shuqn2u5h">
              Fields for {moduleLabel}
            </h2>
            <p className="text-xs text-gray-500 font-mono" data-id="element-2346" data-mp="4vwoz2p6j3lq">GET {fieldsUrl}</p>
            <button onClick={handleFetchFields} disabled={loading !== null} className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2347" data-mp="9ty63i31osuk">
              {loading === 'fields' ? <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-2348" data-mp="truco78zhx15" />
                  Fetching...
                </> : 'Show Fields'}
            </button>
          </div>

          <ResponseBlock response={settingsResponse} error={settingsError} onClear={() => {
        setSettingsResponse(null);
        setSettingsError(null);
      }} data-id="element-2349" data-mp="kpl7lwwhivv5" />
        </div>}
    </div>;
}